import React from 'react';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import Icon from '../../../components/AppIcon';

const BadgeWrapper = styled.span`
  display: inline-block;
  position: relative;

  button {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 8px;
    border: 1px solid var(--br-warning-border);
    border-radius: 4px;
    background: var(--br-warning-surface);
    color: var(--br-warning-text);
    cursor: help;
    font: inherit;
    font-size: 12px;
    white-space: nowrap;
  }

  button:focus-visible {
    outline: 2px solid var(--br-focus);
    outline-offset: 2px;
  }

  [role='tooltip'] {
    position: absolute;
    bottom: calc(100% + 6px);
    left: 0;
    z-index: 10;
    width: max-content;
    max-width: min(280px, 80vw);
    padding: 8px 10px;
    border-radius: 4px;
    background: var(--br-text);
    color: var(--br-surface);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
    white-space: normal;
  }
`;

/**
 * Discreet warning shown next to a reader application when the account's
 * footprint does not add up — a very new account that has taken on a lot of
 * manuscripts and finished none.
 *
 * This is INFORMATION, not enforcement. Nothing is blocked, hidden or
 * auto-declined; the author still approves or declines as usual. The copy stays
 * observational (what the account did) rather than accusatory ("this is a bot"),
 * because a false positive here is a warning shown about a real person.
 *
 * The server decides whether to flag (routes/readers/join-requests/
 * readerActivitySignal.js) so the thresholds live in one env-tunable place; the
 * client only renders what it is given.
 */
export const UnusualActivityBadge = ({ unusualActivity, t }) => {
  const [open, setOpen] = React.useState(false);
  const tooltipId = React.useId();
  if (!unusualActivity) return null;

  const { books = 0, accountAgeDays = 0 } = unusualActivity;
  // "in 0 days" reads badly for a same-day account, and the same-day case is the
  // most striking one, so it gets its own phrasing.
  const tooltip = accountAgeDays === 0
    ? t('UnusualActivityTooltipToday', { books })
    : t('UnusualActivityTooltip', { books, count: accountAgeDays });

  return (
    <BadgeWrapper onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        data-testid="unusual-activity-badge"
        aria-label={t('UnusualActivity')}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onClick={() => setOpen(true)}
        onKeyDown={event => { if (event.key === 'Escape') setOpen(false); }}
      >
        <Icon name="exclamation triangle" />
        {t('UnusualActivity')}
      </button>
      {open && <span id={tooltipId} role="tooltip">{tooltip}</span>}
    </BadgeWrapper>
  );
};

export default withTranslation()(UnusualActivityBadge);
